import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { BookOpen, Edit2, Trash2, Save, Plus, CheckCircle, Languages, Loader2, EyeOff, Eye, RefreshCw } from "lucide-react";
import AdminSidebar from "../components/AdminSidebar";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function QuestionsManagement() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState({ open: false, question: null, section: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, questionId: null });
  const [addMCQDialog, setAddMCQDialog] = useState({ open: false, section: null });
  const [addSubjectiveDialog, setAddSubjectiveDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [secondaryLanguage, setSecondaryLanguage] = useState("Assamese");
  
  // New MCQ question template
  const [newMCQ, setNewMCQ] = useState({
    id: "",
    text: "",
    text_as: "",
    options: ["", "", "", ""],
    correct: "A"
  });
  
  // New Subjective question template
  const [newSubjective, setNewSubjective] = useState({
    id: "",
    text: "",
    text_as: "",
    alt_text: "",
    alt_text_as: "",
    marks: 10
  });

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchQuestions();
    fetchSecondaryLanguage();
  }, [navigate]);

  const fetchSecondaryLanguage = async () => {
    try {
      const res = await axios.get(`${API}/admin/exam-settings`);
      if (res.data?.secondary_language) {
        setSecondaryLanguage(res.data.secondary_language);
      }
    } catch (e) {
      // Fallback to default
    }
  };

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/questions/init`);
      const response = await axios.get(`${API}/admin/questions`);
      setQuestions(response.data);
    } catch (error) {
      toast.error("Failed to load questions");
    } finally {
      setLoading(false);
    }
  };

  const handleEditQuestion = (question, section) => {
    setEditDialog({ open: true, question: { ...question }, section, originalId: question.id });
  };

  const handleSaveQuestion = async () => {
    setSaving(true);
    try {
      const { question, section, originalId } = editDialog;
      
      if (section === "section3") {
        await axios.put(`${API}/admin/questions/subjective/${originalId}`, question);
      } else {
        await axios.put(`${API}/admin/questions/mcq/${originalId}`, question);
      }
      
      toast.success("Question updated successfully");
      setEditDialog({ open: false, question: null, section: null, originalId: null });
      fetchQuestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update question");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteQuestion = async () => {
    try {
      await axios.delete(`${API}/admin/questions/${deleteDialog.questionId}`);
      toast.success("Question deleted successfully");
      setDeleteDialog({ open: false, questionId: null });
      fetchQuestions();
    } catch (error) {
      toast.error("Failed to delete question");
    }
  };

  const handleToggleVisibility = async (questionId, currentHidden) => {
    try {
      await axios.put(`${API}/admin/questions/${questionId}/visibility?hidden=${!currentHidden}`);
      toast.success(currentHidden ? "Question is now visible" : "Question is now hidden");
      fetchQuestions();
    } catch (error) {
      toast.error("Failed to change question visibility");
    }
  };

  const handleTranslateAll = async () => {
    setTranslating(true);
    try {
      const response = await axios.post(`${API}/admin/translate-all`);
      toast.success(`Translated ${response.data.translated_count} items to ${secondaryLanguage}`);
      fetchQuestions();
    } catch (error) {
      toast.error("Translation failed. Please try again.");
    } finally {
      setTranslating(false);
    }
  };

  // Add new MCQ question
  const handleAddMCQ = async () => {
    if (!newMCQ.text.trim()) {
      toast.error("Please enter question text");
      return;
    }
    if (newMCQ.options.some(opt => !opt.trim())) {
      toast.error("Please fill all 4 options");
      return;
    }

    setSaving(true);
    try {
      // Use provided ID or calculate next question ID
      let questionId = parseInt(newMCQ.id);
      if (!questionId || isNaN(questionId)) {
        const sectionQuestions = questions[addMCQDialog.section].questions;
        const maxId = Math.max(...sectionQuestions.map(q => q.id), 0);
        questionId = maxId + 1;
      }

      await axios.post(`${API}/admin/questions/mcq?section=${addMCQDialog.section}`, {
        id: questionId,
        text: newMCQ.text,
        text_as: newMCQ.text_as,
        options: newMCQ.options,
        correct: newMCQ.correct
      });
      
      toast.success("Question added successfully");
      setAddMCQDialog({ open: false, section: null });
      setNewMCQ({ id: "", text: "", text_as: "", options: ["", "", "", ""], correct: "A" });
      fetchQuestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add question");
    } finally {
      setSaving(false);
    }
  };

  // Add new Subjective question
  const handleAddSubjective = async () => {
    if (!newSubjective.text.trim()) {
      toast.error("Please enter main question text");
      return;
    }

    setSaving(true);
    try {
      // Use provided ID or calculate next question ID
      let questionId = parseInt(newSubjective.id);
      if (!questionId || isNaN(questionId)) {
        const sectionQuestions = questions.section3.questions;
        const maxId = Math.max(...sectionQuestions.map(q => q.id), 0);
        questionId = maxId + 1;
      }

      await axios.post(`${API}/admin/questions/subjective`, {
        id: questionId,
        text: newSubjective.text,
        text_as: newSubjective.text_as,
        alt_text: newSubjective.alt_text,
        alt_text_as: newSubjective.alt_text_as,
        marks: newSubjective.marks
      });
      
      toast.success("Subjective question added successfully");
      setAddSubjectiveDialog(false);
      setNewSubjective({ id: "", text: "", text_as: "", alt_text: "", alt_text_as: "", marks: 10 });
      fetchQuestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add question");
    } finally {
      setSaving(false);
    }
  };

  const updateEditQuestion = (field, value) => {
    setEditDialog(prev => ({
      ...prev,
      question: { ...prev.question, [field]: value }
    }));
  };

  const updateEditQuestionOption = (index, value) => {
    setEditDialog(prev => {
      const newOptions = [...prev.question.options];
      newOptions[index] = value;
      return {
        ...prev,
        question: { ...prev.question, options: newOptions }
      };
    });
  };

  const updateNewMCQOption = (index, value) => {
    setNewMCQ(prev => {
      const newOptions = [...prev.options];
      newOptions[index] = value;
      return { ...prev, options: newOptions };
    });
  };

  const optionLetters = ["A", "B", "C", "D"];

  if (loading || !questions) {
    return (
      <AdminSidebar>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-10 h-10 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminSidebar>
    );
  }

  return (
    <AdminSidebar>
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Questions Management</h1>
            <p className="text-sm text-gray-500">Add, edit, or hide exam questions</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchQuestions}
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        <Tabs defaultValue="section1">
          <TabsList className="grid grid-cols-3 w-full max-w-lg mb-6">
            <TabsTrigger value="section1">Section I ({questions.section1.questions.length})</TabsTrigger>
            <TabsTrigger value="section2">Section II ({questions.section2.questions.length})</TabsTrigger>
            <TabsTrigger value="section3">Section III ({questions.section3.questions.length})</TabsTrigger>
          </TabsList>

          {/* Section 1 */}
          <TabsContent value="section1">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>{questions.section1.title}</CardTitle>
                <Button 
                  onClick={() => setAddMCQDialog({ open: true, section: "section1" })}
                  className="bg-emerald-700 hover:bg-emerald-800"
                  data-testid="add-question-section1"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add Question
                </Button>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[600px]">
                  <div className="space-y-4">
                    {questions.section1.questions.map((q) => (
                      <QuestionCard 
                        key={q.id} 
                        question={q} 
                        secondaryLanguage={secondaryLanguage}
                        onEdit={() => handleEditQuestion(q, "section1")}
                        onDelete={() => setDeleteDialog({ open: true, questionId: q.id })}
                        onToggleVisibility={() => handleToggleVisibility(q.id, q.hidden)}
                      />
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Section 2 */}
          <TabsContent value="section2">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>{questions.section2.title}</CardTitle>
                <Button 
                  onClick={() => setAddMCQDialog({ open: true, section: "section2" })}
                  className="bg-emerald-700 hover:bg-emerald-800"
                  data-testid="add-question-section2"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add Question
                </Button>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[600px]">
                  <div className="space-y-4">
                    {questions.section2.questions.map((q) => (
                      <QuestionCard 
                        key={q.id} 
                        question={q} 
                        onEdit={() => handleEditQuestion(q, "section2")}
                        onDelete={() => setDeleteDialog({ open: true, questionId: q.id })}
                        onToggleVisibility={() => handleToggleVisibility(q.id, q.hidden)}
                      />
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Section 3 */}
          <TabsContent value="section3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>{questions.section3.title}</CardTitle>
                <Button 
                  onClick={() => setAddSubjectiveDialog(true)}
                  className="bg-emerald-700 hover:bg-emerald-800"
                  data-testid="add-question-section3"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add Question
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {questions.section3.questions.map((q) => (
                    <SubjectiveQuestionCard 
                      key={q.id} 
                      question={q} 
                      secondaryLanguage={secondaryLanguage}
                      onEdit={() => handleEditQuestion(q, "section3")}
                      onDelete={() => setDeleteDialog({ open: true, questionId: q.id })}
                      onToggleVisibility={() => handleToggleVisibility(q.id, q.hidden)}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

      {/* Add MCQ Question Dialog */}
      <Dialog open={addMCQDialog.open} onOpenChange={(open) => !open && setAddMCQDialog({ open: false, section: null })}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New MCQ Question to {addMCQDialog.section === "section1" ? "Section I" : "Section II"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Question Number (Optional)</Label>
              <Input
                type="number"
                value={newMCQ.id}
                onChange={(e) => setNewMCQ(prev => ({ ...prev, id: e.target.value }))}
                placeholder="Auto-assigned if empty"
                className="mt-1 w-40"
                min="1"
                data-testid="new-mcq-id"
              />
              <p className="text-xs text-gray-500 mt-1">Leave empty to auto-assign the next number</p>
            </div>
            <div>
              <Label>Question Text (English) *</Label>
              <Textarea
                value={newMCQ.text}
                onChange={(e) => setNewMCQ(prev => ({ ...prev, text: e.target.value }))}
                placeholder="Enter the question in English"
                className="mt-1"
                data-testid="new-mcq-text"
              />
            </div>
            <div>
              <Label>Question Text ({secondaryLanguage})</Label>
              <Textarea
                value={newMCQ.text_as}
                onChange={(e) => setNewMCQ(prev => ({ ...prev, text_as: e.target.value }))}
                placeholder={`Enter the question in ${secondaryLanguage} (Optional - can be auto-translated later)`}
                className="mt-1"
              />
            </div>
            
            <div className="space-y-3">
              <Label>Options *</Label>
              {optionLetters.map((letter, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center font-medium text-sm">
                    {letter}
                  </span>
                  <Input
                    value={newMCQ.options[i]}
                    onChange={(e) => updateNewMCQOption(i, e.target.value)}
                    placeholder={`Option ${letter}`}
                    data-testid={`new-mcq-option-${letter}`}
                  />
                </div>
              ))}
            </div>

            <div>
              <Label>Correct Answer *</Label>
              <Select 
                value={newMCQ.correct} 
                onValueChange={(v) => setNewMCQ(prev => ({ ...prev, correct: v }))}
              >
                <SelectTrigger className="mt-1 w-32" data-testid="new-mcq-correct">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {optionLetters.map((letter) => (
                    <SelectItem key={letter} value={letter}>{letter}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddMCQDialog({ open: false, section: null })}>
              Cancel
            </Button>
            <Button onClick={handleAddMCQ} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-new-mcq-btn">
              <Plus className="w-4 h-4 mr-2" /> {saving ? "Adding..." : "Add Question"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Subjective Question Dialog */}
      <Dialog open={addSubjectiveDialog} onOpenChange={setAddSubjectiveDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Subjective Question to Section III</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Question Number (Optional)</Label>
              <Input
                type="number"
                value={newSubjective.id}
                onChange={(e) => setNewSubjective(prev => ({ ...prev, id: e.target.value }))}
                placeholder="Auto-assigned if empty"
                className="mt-1 w-40"
                min="1"
                data-testid="new-subj-id"
              />
              <p className="text-xs text-gray-500 mt-1">Leave empty to auto-assign the next number</p>
            </div>
            <div>
              <Label>Main Question (English) *</Label>
              <Textarea
                value={newSubjective.text}
                onChange={(e) => setNewSubjective(prev => ({ ...prev, text: e.target.value }))}
                placeholder="Enter the main question in English"
                className="mt-1"
                rows={3}
                data-testid="new-subj-text"
              />
            </div>
            <div>
              <Label>Main Question ({secondaryLanguage})</Label>
              <Textarea
                value={newSubjective.text_as}
                onChange={(e) => setNewSubjective(prev => ({ ...prev, text_as: e.target.value }))}
                placeholder={`Enter the main question in ${secondaryLanguage} (Optional)`}
                className="mt-1"
                rows={3}
              />
            </div>
            <div>
              <Label>Alternative Question - OR Option (English)</Label>
              <Textarea
                value={newSubjective.alt_text}
                onChange={(e) => setNewSubjective(prev => ({ ...prev, alt_text: e.target.value }))}
                placeholder="Enter alternative question (the OR option)"
                className="mt-1"
                rows={3}
                data-testid="new-subj-alt-text"
              />
            </div>
            <div>
              <Label>Alternative Question ({secondaryLanguage})</Label>
              <Textarea
                value={newSubjective.alt_text_as}
                onChange={(e) => setNewSubjective(prev => ({ ...prev, alt_text_as: e.target.value }))}
                placeholder={`Alternative question in ${secondaryLanguage} (Optional)`}
                className="mt-1"
                rows={3}
              />
            </div>
            <div>
              <Label>Marks</Label>
              <Input
                type="number"
                value={newSubjective.marks}
                onChange={(e) => setNewSubjective(prev => ({ ...prev, marks: parseInt(e.target.value) || 10 }))}
                className="mt-1 w-24"
                data-testid="new-subj-marks"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddSubjectiveDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddSubjective} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-new-subj-btn">
              <Plus className="w-4 h-4 mr-2" /> {saving ? "Adding..." : "Add Question"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit MCQ Dialog */}
      {editDialog.section !== "section3" && (
        <Dialog open={editDialog.open && editDialog.section !== "section3"} onOpenChange={(open) => !open && setEditDialog({ open: false, question: null, section: null })}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Question</DialogTitle>
            </DialogHeader>
            {editDialog.question && (
              <div className="space-y-4">
                <div>
                  <Label>Question Number</Label>
                  <Input
                    type="number"
                    value={editDialog.question.id}
                    onChange={(e) => updateEditQuestion("id", parseInt(e.target.value) || 1)}
                    className="mt-1 w-32"
                    min="1"
                    data-testid="edit-question-id"
                  />
                  <p className="text-xs text-gray-500 mt-1">Change the question number/order</p>
                </div>
                <div>
                  <Label>Question Text (English)</Label>
                  <Textarea
                    value={editDialog.question.text}
                    onChange={(e) => updateEditQuestion("text", e.target.value)}
                    className="mt-1"
                    data-testid="edit-question-text"
                  />
                </div>
                <div>
                  <Label>Question Text ({secondaryLanguage})</Label>
                  <Textarea
                    value={editDialog.question.text_as || ""}
                    onChange={(e) => updateEditQuestion("text_as", e.target.value)}
                    className="mt-1"
                    placeholder={`${secondaryLanguage} translation will appear here after clicking 'Translate'`}
                    data-testid="edit-question-text-as"
                  />
                </div>
                
                <div className="space-y-3">
                  <Label>Options</Label>
                  {editDialog.question.options?.map((opt, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center font-medium text-sm">
                        {optionLetters[i]}
                      </span>
                      <Input
                        value={opt}
                        onChange={(e) => updateEditQuestionOption(i, e.target.value)}
                        data-testid={`edit-option-${optionLetters[i]}`}
                      />
                    </div>
                  ))}
                </div>

                <div>
                  <Label>Correct Answer</Label>
                  <Select 
                    value={editDialog.question.correct} 
                    onValueChange={(v) => updateEditQuestion("correct", v)}
                  >
                    <SelectTrigger className="mt-1" data-testid="edit-correct-answer">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {optionLetters.map((letter) => (
                        <SelectItem key={letter} value={letter}>{letter}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialog({ open: false, question: null, section: null })}>
                Cancel
              </Button>
              <Button onClick={handleSaveQuestion} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-question-btn">
                <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Subjective Dialog */}
      {editDialog.section === "section3" && (
        <Dialog open={editDialog.open && editDialog.section === "section3"} onOpenChange={(open) => !open && setEditDialog({ open: false, question: null, section: null })}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Subjective Question</DialogTitle>
            </DialogHeader>
            {editDialog.question && (
              <div className="space-y-4">
                <div>
                  <Label>Question Number</Label>
                  <Input
                    type="number"
                    value={editDialog.question.id}
                    onChange={(e) => updateEditQuestion("id", parseInt(e.target.value) || 1)}
                    className="mt-1 w-32"
                    min="1"
                    data-testid="edit-subjective-question-id"
                  />
                  <p className="text-xs text-gray-500 mt-1">Change the question number/order</p>
                </div>
                <div>
                  <Label>Main Question (English)</Label>
                  <Textarea
                    value={editDialog.question.text}
                    onChange={(e) => updateEditQuestion("text", e.target.value)}
                    className="mt-1"
                    rows={3}
                  />
                </div>
                <div>
                  <Label>Main Question ({secondaryLanguage})</Label>
                  <Textarea
                    value={editDialog.question.text_as || ""}
                    onChange={(e) => updateEditQuestion("text_as", e.target.value)}
                    className="mt-1"
                    rows={3}
                    placeholder={`${secondaryLanguage} translation`}
                  />
                </div>
                <div>
                  <Label>Alternative Question (English)</Label>
                  <Textarea
                    value={editDialog.question.alt_text || ""}
                    onChange={(e) => updateEditQuestion("alt_text", e.target.value)}
                    className="mt-1"
                    rows={3}
                  />
                </div>
                <div>
                  <Label>Alternative Question ({secondaryLanguage})</Label>
                  <Textarea
                    value={editDialog.question.alt_text_as || ""}
                    onChange={(e) => updateEditQuestion("alt_text_as", e.target.value)}
                    className="mt-1"
                    rows={3}
                    placeholder={`${secondaryLanguage} translation`}
                  />
                </div>
                <div>
                  <Label>Marks</Label>
                  <Input
                    type="number"
                    value={editDialog.question.marks}
                    onChange={(e) => updateEditQuestion("marks", parseInt(e.target.value) || 10)}
                    className="mt-1 w-24"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialog({ open: false, question: null, section: null })}>
                Cancel
              </Button>
              <Button onClick={handleSaveQuestion} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800">
                <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Question?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this question? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteQuestion} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </AdminSidebar>
  );
}

// MCQ Question Card Component
function QuestionCard({ question, secondaryLanguage, onEdit, onDelete, onToggleVisibility }) {
  const optionLetters = ["A", "B", "C", "D"];
  
  return (
    <div className={`p-4 border rounded-lg hover:shadow-sm transition-shadow ${question.hidden ? "bg-gray-100 opacity-60" : "bg-white"}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-semibold text-emerald-700">Q{question.id}</span>
            <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded">
              Answer: {question.correct}
            </span>
            {question.text_as && (
              <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                {secondaryLanguage} ✓
              </span>
            )}
            {question.hidden && (
              <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded">
                Hidden
              </span>
            )}
          </div>
          <p className="text-gray-800 mb-2">{question.text}</p>
          {question.text_as && (
            <p className="text-sm text-gray-500 mb-3">{question.text_as}</p>
          )}
          <div className="grid grid-cols-2 gap-2 text-sm">
            {question.options?.map((opt, i) => (
              <div 
                key={i} 
                className={`p-2 rounded ${question.correct === optionLetters[i] ? "bg-green-50 text-green-800 border border-green-200" : "bg-gray-50"}`}
              >
                <span className="font-medium mr-2">{optionLetters[i]}.</span>
                {opt}
                {question.correct === optionLetters[i] && <CheckCircle className="w-4 h-4 inline ml-2 text-green-600" />}
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Button 
            size="sm" 
            variant="outline" 
            onClick={onToggleVisibility} 
            className={question.hidden ? "text-green-600 hover:text-green-700" : "text-amber-600 hover:text-amber-700"}
            title={question.hidden ? "Show Question" : "Hide Question"}
            data-testid={`toggle-visibility-${question.id}`}
          >
            {question.hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          </Button>
          <Button size="sm" variant="outline" onClick={onEdit} data-testid={`edit-question-${question.id}`}>
            <Edit2 className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700" onClick={onDelete} data-testid={`delete-question-${question.id}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// Subjective Question Card Component
function SubjectiveQuestionCard({ question, secondaryLanguage, onEdit, onDelete, onToggleVisibility }) {
  return (
    <div className={`p-4 border rounded-lg hover:shadow-sm transition-shadow ${question.hidden ? "bg-gray-100 opacity-60" : "bg-white"}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-semibold text-teal-700">Q{question.id}</span>
            <span className="text-xs px-2 py-0.5 bg-teal-100 text-teal-700 rounded">
              {question.marks} Marks
            </span>
            {question.text_as && (
              <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                {secondaryLanguage} ✓
              </span>
            )}
            {question.hidden && (
              <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded">
                Hidden
              </span>
            )}
          </div>
          
          <div className="space-y-3">
            <div className="p-3 bg-gray-50 rounded">
              <p className="text-xs text-gray-500 mb-1">Main Question:</p>
              <p className="text-gray-800">{question.text}</p>
            </div>
            
            {question.alt_text && (
              <div className="p-3 bg-gray-50 rounded">
                <p className="text-xs text-gray-500 mb-1">OR Alternative:</p>
                <p className="text-gray-800">{question.alt_text}</p>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Button 
            size="sm" 
            variant="outline" 
            onClick={onToggleVisibility} 
            className={question.hidden ? "text-green-600 hover:text-green-700" : "text-amber-600 hover:text-amber-700"}
            title={question.hidden ? "Show Question" : "Hide Question"}
            data-testid={`toggle-visibility-${question.id}`}
          >
            {question.hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          </Button>
          <Button size="sm" variant="outline" onClick={onEdit} data-testid={`edit-question-${question.id}`}>
            <Edit2 className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700" onClick={onDelete} data-testid={`delete-question-${question.id}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
